import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Client, ClientStatus } from '../../../core/models/Client';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import { UserRole } from '../../../core/models/User';

interface ClientFormProps {
  initialData?: Partial<Client>;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const { organizationId, currentUser } = useAuth();
  const { organizations } = useOrganizations();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: initialData?.name || '',
      status: initialData?.status || ClientStatus.ACTIVE,
      organizationId: initialData?.organizationId || organizationId || '',
    },
  });

  const status = watch('status');
  const selectedOrganizationId = watch('organizationId');

  const handleFormSubmit = async (data: any) => {
    await onSubmit({
      name: data.name,
      status: data.status,
      organizationId: isSuperAdmin ? selectedOrganizationId : organizationId,
      clientId: isSuperAdmin ? selectedOrganizationId : organizationId,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isSuperAdmin && (
          <div className="md:col-span-2 space-y-2">
            <label className="text-sm font-medium">Client Account</label>
            <Select
              value={selectedOrganizationId}
              onValueChange={(value) => setValue('organizationId', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select client account" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Input
          label="Project Name"
          {...register('name', { required: 'Project name is required' })}
          error={errors.name?.message}
        />

        <div className="space-y-2">
          <label className="text-sm font-medium">Status</label>
          <Select
            value={status}
            onValueChange={(value) => setValue('status', value as ClientStatus)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ClientStatus.ACTIVE}>Active</SelectItem>
              <SelectItem value={ClientStatus.INACTIVE}>Inactive</SelectItem>
              <SelectItem value={ClientStatus.PENDING}>Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Contact details (email, phone, address) come from the linked client account.
      </p>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {initialData ? 'Update Project' : 'Create Project'}
        </Button>
      </div>
    </form>
  );
};
